import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { API_BASE_URL } from '../../config';
import './PropertyDetailPage.css';

// ─── Helpers ─────────────────────────────────────────────────────────────────
const fmt = (price) =>
    price
        ? new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0 }).format(price)
        : null;

const tryParse = (json) => {
    if (!json) return [];
    if (Array.isArray(json)) return json;
    try { return JSON.parse(json); } catch { return []; }
};

// ─── Image Gallery ───────────────────────────────────────────────────────────
const ImageGallery = ({ images }) => {
    const [active, setActive] = useState(0);
    const [lightbox, setLightbox] = useState(false);

    if (!images || images.length === 0) {
        return (
            <div className="pdp-gallery pdp-gallery-empty">
                <div className="pdp-gallery-placeholder">
                    <svg viewBox="0 0 100 70" fill="none">
                        <rect width="100" height="70" fill="#dde3f0" />
                        <path d="M25 52V34l25-18 25 18v18" stroke="#8fa3cc" strokeWidth="3" fill="none" strokeLinejoin="round" />
                        <rect x="41" y="37" width="18" height="15" rx="1.5" fill="#8fa3cc" />
                        <rect x="27" y="34" width="12" height="10" rx="1" fill="#b0bfe0" />
                        <rect x="61" y="34" width="12" height="10" rx="1" fill="#b0bfe0" />
                    </svg>
                    <span>No photos available yet</span>
                </div>
            </div>
        );
    }

    const prev = () => setActive(a => (a - 1 + images.length) % images.length);
    const next = () => setActive(a => (a + 1) % images.length);

    return (
        <>
            <div className="pdp-gallery">
                {/* Main image */}
                <div className="pdp-gallery-main" onClick={() => setLightbox(true)}>
                    <img src={images[active].url} alt={images[active].caption || 'Property photo'} />
                    <div className="pdp-gallery-counter">{active + 1} / {images.length}</div>
                    {images.length > 1 && (
                        <>
                            <button className="pdp-nav-btn pdp-nav-prev" onClick={(e) => { e.stopPropagation(); prev(); }}>
                                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                    <polyline points="15 18 9 12 15 6" />
                                </svg>
                            </button>
                            <button className="pdp-nav-btn pdp-nav-next" onClick={(e) => { e.stopPropagation(); next(); }}>
                                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                    <polyline points="9 18 15 12 9 6" />
                                </svg>
                            </button>
                        </>
                    )}
                    <button className="pdp-gallery-expand">
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <polyline points="15 3 21 3 21 9" /><polyline points="9 21 3 21 3 15" />
                            <line x1="21" y1="3" x2="14" y2="10" /><line x1="3" y1="21" x2="10" y2="14" />
                        </svg>
                        View all photos
                    </button>
                </div>

                {/* Thumbnails */}
                {images.length > 1 && (
                    <div className="pdp-gallery-thumbs">
                        {images.map((img, i) => (
                            <button
                                key={img.id || i}
                                className={`pdp-thumb ${i === active ? 'active' : ''}`}
                                onClick={() => setActive(i)}
                            >
                                <img src={img.url} alt={img.caption || `Photo ${i + 1}`} />
                            </button>
                        ))}
                    </div>
                )}
            </div>

            {/* Lightbox */}
            {lightbox && (
                <div className="pdp-lightbox" onClick={() => setLightbox(false)}>
                    <button className="pdp-lightbox-close" onClick={() => setLightbox(false)}>×</button>
                    <button className="pdp-lightbox-nav left" onClick={(e) => { e.stopPropagation(); prev(); }}>
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                            <polyline points="15 18 9 12 15 6" />
                        </svg>
                    </button>
                    <img
                        src={images[active].url}
                        alt={images[active].caption || 'Property photo'}
                        onClick={e => e.stopPropagation()}
                    />
                    <button className="pdp-lightbox-nav right" onClick={(e) => { e.stopPropagation(); next(); }}>
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                            <polyline points="9 18 15 12 9 6" />
                        </svg>
                    </button>
                    {images[active].caption && (
                        <div className="pdp-lightbox-caption">{images[active].caption}</div>
                    )}
                </div>
            )}
        </>
    );
};

// ─── Fact Chip ───────────────────────────────────────────────────────────────
const Fact = ({ icon, label, value }) => (
    value ? (
        <div className="pdp-fact">
            <span className="pdp-fact-icon">{icon}</span>
            <div>
                <div className="pdp-fact-label">{label}</div>
                <div className="pdp-fact-value">{value}</div>
            </div>
        </div>
    ) : null
);

// ─── Unit Card ───────────────────────────────────────────────────────────────
const UnitCard = ({ unit, onApply }) => {
    const amenities = tryParse(unit.amenities);
    const appliances = tryParse(unit.appliances);

    return (
        <div className="pdp-unit-card">
            <div className="pdp-unit-header">
                <div>
                    <div className="pdp-unit-number">Unit {unit.unit_number}</div>
                    {unit.unit_type_name && (
                        <div className="pdp-unit-type">{unit.unit_type_name}</div>
                    )}
                </div>
                <div className="pdp-unit-rent">
                    {fmt(unit.monthly_rent) || 'Contact'}
                    <span className="pdp-unit-rent-period">/mo</span>
                </div>
            </div>

            <div className="pdp-unit-specs">
                {unit.bedrooms !== null && unit.bedrooms !== undefined && (
                    <span className="pdp-unit-spec">
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" /><polyline points="9 22 9 12 15 12 15 22" />
                        </svg>
                        {unit.bedrooms === 0 ? 'Studio' : `${unit.bedrooms} bed`}
                    </span>
                )}
                {unit.bathrooms && (
                    <span className="pdp-unit-spec">
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M9 6 6.5 3.5a1.5 1.5 0 0 0-1-.5C4.683 3 4 3.683 4 4.5V17a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-5" /><line x1="2" y1="12" x2="22" y2="12" />
                        </svg>
                        {unit.bathrooms} bath
                    </span>
                )}
                {unit.square_footage && (
                    <span className="pdp-unit-spec">
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <polyline points="15 3 21 3 21 9" /><polyline points="9 21 3 21 3 15" /><line x1="21" y1="3" x2="14" y2="10" /><line x1="3" y1="21" x2="10" y2="14" />
                        </svg>
                        {unit.square_footage.toLocaleString()} sqft
                    </span>
                )}
                {unit.floor_number && (
                    <span className="pdp-unit-spec">Floor {unit.floor_number}</span>
                )}
                {unit.available_date && (
                    <span className="pdp-unit-spec avail">
                        Available {new Date(unit.available_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                    </span>
                )}
            </div>

            {(amenities.length > 0 || appliances.length > 0) && (
                <div className="pdp-unit-tags">
                    {[...amenities.slice(0, 4), ...appliances.slice(0, 3)].map((tag, i) => (
                        <span key={i} className="pdp-unit-tag">{tag}</span>
                    ))}
                </div>
            )}

            {unit.description && (
                <p className="pdp-unit-desc">{unit.description}</p>
            )}

            <div className="pdp-unit-footer">
                {unit.deposit_amount && (
                    <span className="pdp-unit-deposit">Deposit: {fmt(unit.deposit_amount)}</span>
                )}
                <button className="pdp-apply-unit-btn" onClick={() => onApply(unit.id)}>
                    Apply for This Unit
                </button>
            </div>
        </div>
    );
};

// ─── Main Component ──────────────────────────────────────────────────────────
const PropertyDetailPage = () => {
    const { propertyId } = useParams();
    const navigate = useNavigate();
    const [property, setProperty]   = useState(null);
    const [allUnits, setAllUnits]   = useState([]);
    const [images, setImages]       = useState([]);
    const [loading, setLoading]     = useState(true);
    const [error, setError]         = useState(null);
    const [bedsFilter, setBedsFilter] = useState('');
    const [bathsFilter, setBathsFilter] = useState('');
    const [mapLoaded, setMapLoaded] = useState(false);

    useEffect(() => { fetchAll(); }, [propertyId]);

    const fetchAll = async () => {
        try {
            setLoading(true);
            setError(null);

            const [propRes, unitsRes, imagesRes] = await Promise.all([
                fetch(`${API_BASE_URL}/api/properties/${propertyId}`),
                fetch(`${API_BASE_URL}/api/properties/${propertyId}/units`),
                fetch(`${API_BASE_URL}/api/properties/${propertyId}/images`)
            ]);

            if (!propRes.ok) throw new Error('Property not found');

            const [propData, unitsData, imagesData] = await Promise.all([
                propRes.json(),
                unitsRes.ok ? unitsRes.json() : [],
                imagesRes.ok ? imagesRes.json() : []
            ]);

            setProperty(propData);
            setAllUnits(unitsData);
            setImages(imagesData);

            // Auto-geocode if lat/lng missing
            if (propData.address && !propData.latitude) {
                geocodeAddress(propData.address, propData);
            }
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const geocodeAddress = async (address, propData) => {
        try {
            const encoded = encodeURIComponent(address);
            const res = await fetch(`https://nominatim.openstreetmap.org/search?q=${encoded}&format=json&limit=1`, {
                headers: { 'Accept-Language': 'en' }
            });
            const data = await res.json();
            if (data && data[0]) {
                setProperty(p => ({ ...p, latitude: parseFloat(data[0].lat), longitude: parseFloat(data[0].lon) }));
            }
        } catch {}
    };

    const handleApply = (unitId = null) => {
        const params = new URLSearchParams({ propertyId });
        if (unitId) params.append('unitId', unitId);
        navigate(`/apply?${params.toString()}`);
    };

    const availableUnits = allUnits.filter(u => u.status === 'vacant' && u.monthly_rent != null);
    const filteredUnits = availableUnits.filter(u => {
        if (bedsFilter !== '' && (u.bedrooms == null || u.bedrooms < parseInt(bedsFilter))) return false;
        if (bathsFilter !== '' && (u.bathrooms == null || u.bathrooms < parseFloat(bathsFilter))) return false;
        return true;
    });

    const propertyAmenities = tryParse(property?.amenities);
    const prices = availableUnits.map(u => u.monthly_rent).filter(Boolean);
    const minPrice = prices.length ? Math.min(...prices) : null;
    const maxPrice = prices.length ? Math.max(...prices) : null;

    const priceDisplay = minPrice
        ? (minPrice === maxPrice ? `${fmt(minPrice)}/mo` : `${fmt(minPrice)} – ${fmt(maxPrice)}/mo`)
        : 'Contact for pricing';

    if (loading) {
        return (
            <div className="pdp-page">
                <div className="pdp-loading">
                    <div className="pdp-spinner" />
                    <p>Loading property details…</p>
                </div>
            </div>
        );
    }

    if (error || !property) {
        return (
            <div className="pdp-page">
                <div className="pdp-error">
                    <h2>Property Not Found</h2>
                    <p>{error}</p>
                    <Link to="/listings" className="pdp-back-link">← Back to Listings</Link>
                </div>
            </div>
        );
    }

    return (
        <div className="pdp-page">
            {/* ── Breadcrumb ────────────────────────────────── */}
            <div className="pdp-breadcrumb">
                <div className="pdp-breadcrumb-inner">
                    <Link to="/listings" className="pdp-back-link">
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                            <polyline points="15 18 9 12 15 6" />
                        </svg>
                        Back to Listings
                    </Link>
                    <span className="pdp-breadcrumb-sep">/</span>
                    <span>{property.property_name}</span>
                </div>
            </div>

            {/* ── Gallery ───────────────────────────────────── */}
            <ImageGallery images={images} />

            {/* ── Body ──────────────────────────────────────── */}
            <div className="pdp-body">
                <div className="pdp-main">

                    {/* Hero info */}
                    <div className="pdp-hero-info">
                        <div>
                            <div className="pdp-price-hero">{priceDisplay}</div>
                            <h1 className="pdp-property-name">{property.property_name}</h1>
                            <div className="pdp-address">
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" /><circle cx="12" cy="10" r="3" />
                                </svg>
                                {property.address}
                                {property.neighborhood && ` · ${property.neighborhood}`}
                            </div>
                        </div>
                        <div className="pdp-hero-badges">
                            {availableUnits.length > 0 && (
                                <span className="pdp-badge available">{availableUnits.length} Unit{availableUnits.length > 1 ? 's' : ''} Available</span>
                            )}
                            {property.property_type && (
                                <span className="pdp-badge type">{property.property_type}</span>
                            )}
                        </div>
                    </div>

                    {/* Quick stats bar */}
                    <div className="pdp-stats-bar">
                        {property.year_built && <div className="pdp-stat"><span>Year Built</span><strong>{property.year_built}</strong></div>}
                        {property.unit_count && <div className="pdp-stat"><span>Total Units</span><strong>{property.unit_count}</strong></div>}
                        {property.total_square_footage && <div className="pdp-stat"><span>Total Sq Ft</span><strong>{Number(property.total_square_footage).toLocaleString()}</strong></div>}
                        {property.property_manager && <div className="pdp-stat"><span>Managed by</span><strong>{property.property_manager}</strong></div>}
                    </div>

                    {/* Description */}
                    {property.description && (
                        <section className="pdp-section">
                            <h2>About This Property</h2>
                            <p className="pdp-description">{property.description}</p>
                        </section>
                    )}

                    {/* Facts & Features */}
                    <section className="pdp-section">
                        <h2>Facts &amp; Features</h2>
                        <div className="pdp-facts-grid">
                            {/* Type & basics */}
                            <div className="pdp-facts-group">
                                <div className="pdp-facts-group-title">Property Details</div>
                                <Fact icon="🏢" label="Property Type" value={property.property_type} />
                                <Fact icon="📅" label="Year Built" value={property.year_built} />
                                <Fact icon="📐" label="Total Sq Ft" value={property.total_square_footage ? `${Number(property.total_square_footage).toLocaleString()} sqft` : null} />
                                <Fact icon="🏠" label="Total Units" value={property.unit_count ? String(property.unit_count) : null} />
                                <Fact icon="📋" label="Status" value={property.property_status} />
                            </div>

                            {/* HVAC & Utilities */}
                            <div className="pdp-facts-group">
                                <div className="pdp-facts-group-title">Utilities &amp; HVAC</div>
                                {allUnits[0]?.heat_type && <Fact icon="🔥" label="Heating" value={allUnits[0].heat_type} />}
                                {allUnits[0]?.cooling_type && <Fact icon="❄️" label="Cooling" value={allUnits[0].cooling_type} />}
                                {allUnits[0]?.laundry_type && <Fact icon="🧺" label="Laundry" value={allUnits[0].laundry_type} />}
                                {allUnits[0]?.parking_type && <Fact icon="🚗" label="Parking" value={allUnits[0].parking_type} />}
                                {property.parking && <Fact icon="🅿️" label="Parking" value={property.parking.split(',').map(s => s.trim()).map(s => s.charAt(0).toUpperCase() + s.slice(1).toLowerCase()).join(', ')} />}
                            </div>

                            {/* Pet & Policies */}
                            <div className="pdp-facts-group">
                                <div className="pdp-facts-group-title">Policies</div>
                                <Fact icon="🐾" label="Pet Policy" value={property.pet_policy === 'allowed' ? 'Pets allowed' : (property.pet_policy === 'not allowed' ? 'Pets not allowed' : property.pet_policy)} />
                                {allUnits[0]?.pet_friendly != null && (
                                    <Fact icon="🐕" label="Pet Friendly" value={allUnits[0].pet_friendly ? 'Yes' : 'No'} />
                                )}
                                {allUnits[0]?.furnished != null && allUnits[0]?.furnished !== undefined && (
                                    <Fact icon="🛋️" label="Furnished" value={allUnits[0].furnished ? 'Yes' : 'No'} />
                                )}
                                {property.maintenance_contact && (
                                    <Fact icon="🔧" label="Maintenance" value={property.maintenance_contact} />
                                )}
                            </div>
                        </div>
                    </section>

                    {/* Amenities */}
                    {propertyAmenities.length > 0 && (
                        <section className="pdp-section">
                            <h2>Amenities</h2>
                            <div className="pdp-amenities-grid">
                                {propertyAmenities.map((a, i) => (
                                    <div key={i} className="pdp-amenity-item">
                                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#1457f5" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                            <polyline points="20 6 9 17 4 12" />
                                        </svg>
                                        {a}
                                    </div>
                                ))}
                            </div>
                        </section>
                    )}

                    {/* Unit appliances aggregated */}
                    {(() => {
                        const allAppliances = [...new Set(allUnits.flatMap(u => tryParse(u.appliances)))];
                        return allAppliances.length > 0 ? (
                            <section className="pdp-section">
                                <h2>Appliances</h2>
                                <div className="pdp-amenities-grid">
                                    {allAppliances.map((a, i) => (
                                        <div key={i} className="pdp-amenity-item">
                                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#1457f5" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                                <polyline points="20 6 9 17 4 12" />
                                            </svg>
                                            {a}
                                        </div>
                                    ))}
                                </div>
                            </section>
                        ) : null;
                    })()}

                    {/* Available Units */}
                    <section className="pdp-section" id="units">
                        <div className="pdp-units-header">
                            <h2>Available Units <span className="pdp-units-count">({filteredUnits.length})</span></h2>
                            <div className="pdp-unit-filters">
                                <select value={bedsFilter} onChange={e => setBedsFilter(e.target.value)}>
                                    <option value="">Any Beds</option>
                                    <option value="0">Studio+</option>
                                    <option value="1">1+ Bed</option>
                                    <option value="2">2+ Beds</option>
                                    <option value="3">3+ Beds</option>
                                </select>
                                <select value={bathsFilter} onChange={e => setBathsFilter(e.target.value)}>
                                    <option value="">Any Baths</option>
                                    <option value="1">1+ Bath</option>
                                    <option value="1.5">1.5+ Bath</option>
                                    <option value="2">2+ Baths</option>
                                </select>
                            </div>
                        </div>

                        {filteredUnits.length === 0 ? (
                            <div className="pdp-no-units">
                                <p>No units match your selected filters.</p>
                                <button onClick={() => { setBedsFilter(''); setBathsFilter(''); }}>
                                    Clear filters
                                </button>
                            </div>
                        ) : (
                            <div className="pdp-units-list">
                                {filteredUnits.map(unit => (
                                    <UnitCard key={unit.id} unit={unit} onApply={handleApply} />
                                ))}
                            </div>
                        )}
                    </section>

                    {/* Location map */}
                    {property.latitude && property.longitude && (
                        <section className="pdp-section">
                            <h2>Location</h2>
                            <div className="pdp-map-container">
                                <iframe
                                    title="Property location map"
                                    width="100%"
                                    height="320"
                                    style={{ border: 'none', borderRadius: '12px' }}
                                    src={`https://www.openstreetmap.org/export/embed.html?bbox=${property.longitude - 0.01}%2C${property.latitude - 0.01}%2C${property.longitude + 0.01}%2C${property.latitude + 0.01}&layer=mapnik&marker=${property.latitude}%2C${property.longitude}`}
                                />
                                <a
                                    href={`https://www.openstreetmap.org/?mlat=${property.latitude}&mlon=${property.longitude}&zoom=15`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="pdp-map-link"
                                >
                                    View larger map →
                                </a>
                            </div>
                            <p className="pdp-location-note">
                                📍 {property.address}
                            </p>
                        </section>
                    )}

                    {/* Schools Nearby placeholder */}
                    <section className="pdp-section">
                        <h2>Schools &amp; Nearby</h2>
                        <div className="pdp-schools-notice">
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#6b7280" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" /><polyline points="9 22 9 12 15 12 15 22" />
                            </svg>
                            <div>
                                <strong>School &amp; transit data</strong>
                                <p>
                                    Search schools near <a href={`https://www.greatschools.org/search/search.page?q=${encodeURIComponent(property.address || '')}`} target="_blank" rel="noopener noreferrer">this address on GreatSchools.org</a> or check <a href={`https://www.walkscore.com/score/${encodeURIComponent(property.address || '')}`} target="_blank" rel="noopener noreferrer">Walk Score</a> for walkability, transit &amp; bike scores.
                                </p>
                            </div>
                        </div>
                    </section>
                </div>

                {/* ── Sidebar ─────────────────────────────────── */}
                <aside className="pdp-sidebar">
                    <div className="pdp-sidebar-card">
                        <div className="pdp-sidebar-price">{priceDisplay}</div>
                        {availableUnits.length > 0 && (
                            <div className="pdp-sidebar-avail">
                                {availableUnits.length} unit{availableUnits.length > 1 ? 's' : ''} available
                            </div>
                        )}

                        <button className="pdp-btn-apply" onClick={() => handleApply()}>
                            Apply Now
                        </button>

                        <a
                            href={`#units`}
                            className="pdp-btn-units"
                            onClick={e => {
                                e.preventDefault();
                                document.getElementById('units')?.scrollIntoView({ behavior: 'smooth' });
                            }}
                        >
                            View All Units
                        </a>

                        {property.maintenance_phone && (
                            <div className="pdp-sidebar-contact">
                                <div className="pdp-sidebar-contact-label">Maintenance</div>
                                <a href={`tel:${property.maintenance_phone}`} className="pdp-sidebar-phone">
                                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                        <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.69 13a19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 3.6 2h3a2 2 0 0 1 2 1.72..." />
                                    </svg>
                                    {property.maintenance_phone}
                                </a>
                            </div>
                        )}

                        {property.property_manager && (
                            <div className="pdp-sidebar-manager">
                                <div className="pdp-sidebar-manager-label">Property Manager</div>
                                <div className="pdp-sidebar-manager-name">{property.property_manager}</div>
                            </div>
                        )}
                    </div>

                    {/* Quick facts sidebar card */}
                    <div className="pdp-sidebar-card pdp-sidebar-facts">
                        <div className="pdp-sidebar-facts-title">Quick Facts</div>
                        {property.year_built && <div className="pdp-sf-row"><span>Year Built</span><strong>{property.year_built}</strong></div>}
                        {property.property_type && <div className="pdp-sf-row"><span>Type</span><strong>{property.property_type}</strong></div>}
                        {minPrice && <div className="pdp-sf-row"><span>Starting at</span><strong>{fmt(minPrice)}/mo</strong></div>}
                        {property.total_square_footage && <div className="pdp-sf-row"><span>Building Sqft</span><strong>{Number(property.total_square_footage).toLocaleString()}</strong></div>}
                        {allUnits[0]?.pet_friendly != null && (
                            <div className="pdp-sf-row"><span>Pet Friendly</span><strong>{allUnits[0].pet_friendly ? 'Yes' : 'No'}</strong></div>
                        )}
                        {allUnits[0]?.parking_type && <div className="pdp-sf-row"><span>Parking</span><strong>{allUnits[0].parking_type}</strong></div>}
                        {allUnits[0]?.laundry_type && <div className="pdp-sf-row"><span>Laundry</span><strong>{allUnits[0].laundry_type}</strong></div>}
                    </div>
                </aside>
            </div>
        </div>
    );
};

export default PropertyDetailPage;
